// T64: animated typing placeholder for the header search. One shared ticker for every mounted search input; it writes the
// placeholder through the DOM (no React state per character), only to visible, unfocused, empty inputs, and pauses in hidden tabs.
export const STATIC_PLACEHOLDER = "Search part numbers, brands, products…";
export const PHRASES = [
  "Search the brands you know and trust...", "Search hardware that means business...", "Search deals worth a double take...",
  "Search the latest laptop deals...", "Search business laptops that deliver...", "Search clearance deals before they're gone...",
  "Search your next tech upgrade...", "Search big brands at better prices...", "Search desktops that do the heavy lifting...",
  "Search docking stations that connect it all...", "Search servers built for bigger things...", "Search upgrades that make a difference...",
];
export const CHAT_STATIC = "Ask about any product or need…";
export const CHAT_PHRASES = [  // T75: Rhyte AI input, natural shopper questions
  "What's a good laptop for working from home?", "I need 10 monitors for our new office...", "Which dock works with my USB-C laptop?",
  "What's the best printer for a small office?", "Do you have i7 laptops under $2,000?", "What UPS do I need for a small server?",
  "Help me set up Wi-Fi for 20 people...", "Which headset is best for Teams calls?", "I need a NAS for our backups...",
  "What's the difference between these two laptops?",
];
const shuffle = (a) => { const b = [...a]; for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [b[i], b[j]] = [b[j], b[i]]; } return b; };
const reduced = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const visible = (el) => el.isConnected && el.offsetParent !== null && !el.value;

// T75: the animated text is an aria-hidden overlay laid over the input's content box (no CLS). It scrolls like typed text, so the
// end being typed always stays visible on narrow inputs and long phrases never cut off mid-word at the right edge.
function place(el, ov) {
  const cs = getComputedStyle(el), px = (v) => parseFloat(v) || 0;
  const l = px(cs.paddingLeft) + px(cs.borderLeftWidth), r = px(cs.paddingRight) + px(cs.borderRightWidth);
  Object.assign(ov.style, { left: `${el.offsetLeft + l}px`, top: `${el.offsetTop}px`, width: `${Math.max(el.offsetWidth - l - r, 0)}px`,
    height: `${el.offsetHeight}px`, fontSize: cs.fontSize, fontFamily: cs.fontFamily, fontWeight: cs.fontWeight, letterSpacing: cs.letterSpacing });
}
function show(ov, text) {
  ov.firstChild.textContent = text;
  ov.scrollLeft = ov.scrollWidth;
  ov.classList.toggle("rs-typing-clip", ov.scrollWidth > ov.clientWidth + 1);
}

function makeTyper(phrases, keepOnFocus = false) {  // keepOnFocus (chat): keeps animating while focused and empty, until the first keystroke
  const order = shuffle(phrases);  // once per page load; loops in this order
  const inputs = new Map();  // input -> overlay
  const st = { i: 0, n: 0, phase: "type", t: null };
  const active = (el) => visible(el) && (keepOnFocus || document.activeElement !== el);
  const text = () => order[st.i].slice(0, st.n);
  const paint = () => inputs.forEach((ov, el) => show(ov, active(el) ? text() : ""));
  function tick() {
    st.t = null;
    if (document.hidden || ![...inputs.keys()].some(active)) { paint(); return; }  // clears a stale overlay (value set from code), resumes on focus-out / visibility / mount
    const p = order[st.i];
    let wait;
    if (st.phase === "type") {
      st.n += 1; paint();
      if (st.n >= p.length) { st.phase = "hold"; wait = 1200; } else wait = 35 + Math.random() * 10;
    } else if (st.phase === "hold") {
      st.phase = "del"; st.n -= 1; paint(); wait = 15 + Math.random() * 5;
    } else {
      st.n -= 1; paint();
      if (st.n <= 0) { st.n = 0; st.i = (st.i + 1) % order.length; st.phase = "type"; }
      wait = st.phase === "type" ? 35 + Math.random() * 10 : 15 + Math.random() * 5;
    }
    st.t = setTimeout(tick, wait);
  }
  const wake = () => { if (!st.t && !reduced()) st.t = setTimeout(tick, 40); };
  document.addEventListener?.("visibilitychange", wake);
  window.addEventListener?.("resize", () => inputs.forEach((ov, el) => place(el, ov)));
  return function attach(el, staticText) {
    if (reduced()) { el.placeholder = staticText; return () => {}; }  // static fallback
    const ov = document.createElement("span");
    ov.className = "rs-typing";
    ov.setAttribute("aria-hidden", "true");
    ov.setAttribute("data-testid", `${el.getAttribute("data-testid") || "input"}-typing`);
    ov.appendChild(document.createElement("span"));
    el.insertAdjacentElement("afterend", ov);
    el.placeholder = "";  // the overlay is the visual placeholder; the input's label / aria-label carries the meaning
    inputs.set(el, ov);
    place(el, ov);
    const ro = window.ResizeObserver ? new ResizeObserver(() => place(el, ov)) : null;  // re-place when the input's box settles or changes
    ro?.observe(el);
    const off = () => show(ov, "");  // nothing once typed in (and, for search, while focused)
    const blur = () => { if (!el.value) { place(el, ov); show(ov, text()); wake(); } };
    if (!keepOnFocus) el.addEventListener("focus", off);
    el.addEventListener("input", off); el.addEventListener("blur", blur);
    show(ov, active(el) ? text() : "");
    wake();
    return () => { ro?.disconnect(); inputs.delete(el); ov.remove(); el.removeEventListener("focus", off); el.removeEventListener("input", off); el.removeEventListener("blur", blur); };
  };
}
const searchTyper = makeTyper(PHRASES), chatTyper = makeTyper(CHAT_PHRASES, true);
export const attachTyping = (el) => searchTyper(el, STATIC_PLACEHOLDER);
export const attachChatTyping = (el) => chatTyper(el, CHAT_STATIC);
